import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Clock, MessageCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../lib/apiError';

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingInfo = location.state?.bookingInfo;
  const [processing, setProcessing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [supportContact, setSupportContact] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (!bookingInfo) {
      navigate('/teachers');
      return;
    }
    fetchSettings();
  }, [bookingInfo, navigate]);

  const fetchSettings = async () => {
    try {
      const [settingsRes, contactRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/dashboard/support-contact`),
      ]);
      setSettings(settingsRes.data);
      setSupportContact(contactRes.data);
    } catch (error) {
      console.warn('Settings load failed (non-critical):', error?.message);
    }
  };

  const sanitizeWhatsappNumber = (number = '') => number.replace(/[^0-9]/g, '');

  const handlePaymentComplete = async () => {
    setProcessing(true);
    try {
      const bookingPayload = {
        teacher_id: bookingInfo.teacher_id,
        subject: bookingInfo.subject,
        section: bookingInfo.section || null,
        chapter: bookingInfo.chapter || null,
        booking_date: bookingInfo.booking_date,
        start_time: bookingInfo.start_time,
        duration_hours: bookingInfo.duration_hours,
        group_size: bookingInfo.group_size || 1,
        visibility: bookingInfo.visibility || 'invite_only',
        expected_amount: bookingInfo.total_amount
      };

      const bookingResponse = await axios.post(`${API}/bookings`, bookingPayload);
      const bookingId = bookingResponse.data.id;

      // Submit for Finance verification — booking is NOT auto-confirmed
      await axios.post(`${API}/bookings/${bookingId}/submit-payment`);

      setSubmitted(true);
      toast.success('Payment submitted! Our team will verify it shortly.');
    } catch (error) {
      toast.error(apiError(error, 'Could not submit payment. Please try again.'));
      setProcessing(false);
    }
  };

  if (!bookingInfo) {
    return null;
  }

  const supportCard = (
    <div className="border-t border-slate-200 pt-4">
      <h4 className="text-sm font-semibold text-slate-900 mb-2">
        {supportContact?.source === 'admin' ? 'Need help? Chat with our admin team' : 'Need help? Chat with your executive'}
      </h4>
      <div className="text-sm text-slate-600 space-y-1" data-testid="payment-executive-details">
        <p data-testid="payment-executive-name">{supportContact?.name || 'Contact not set'}</p>
        <p data-testid="payment-executive-whatsapp">{supportContact?.whatsapp || 'WhatsApp number not set'}</p>
        {supportContact?.availability_slots?.length > 0 && (
          <div data-testid="payment-executive-availability">
            <p className="text-xs text-slate-500">Availability</p>
            <ul className="list-disc list-inside text-xs text-slate-500">
              {supportContact.availability_slots.map((slot, index) => (
                <li key={`${slot}-${index}`} data-testid={`payment-executive-slot-${index}`}>{slot}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {supportContact?.whatsapp ? (
        <Button
          asChild
          className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
          data-testid="payment-whatsapp-button"
        >
          <a
            href={`https://wa.me/${sanitizeWhatsappNumber(supportContact.whatsapp)}?text=${encodeURIComponent(
              `Hi ${supportContact.name || 'there'}, I need help with my booking/payment on WISE.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat on WhatsApp
          </a>
        </Button>
      ) : (
        <Button className="w-full mt-3 rounded-full" variant="outline" disabled data-testid="payment-whatsapp-disabled">
          WhatsApp contact not available
        </Button>
      )}
    </div>
  );

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="payment-processing-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-0 shadow-xl mb-6">
            <CardContent className="py-12 text-center">
              <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-14 h-14 text-amber-600 animate-spin" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3" data-testid="payment-processing-title">
                Booking is being processed
              </h2>
              <p className="text-slate-600 max-w-lg mx-auto leading-relaxed" data-testid="payment-processing-message">
                You will receive confirmation here and by email once confirmed.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-full px-4 py-2">
                <Mail className="w-4 h-4" />
                Our team is verifying your payment.
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/my-bookings')}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8"
                  data-testid="processing-view-bookings-button"
                >
                  View My Bookings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Have a question while you wait?</CardTitle>
            </CardHeader>
            <CardContent>{supportCard}</CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="payment-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Complete Payment</h1>
        <p className="text-lg text-slate-600">Scan the QR code to pay via UPI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Booking Summary */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Booking Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Teacher:</span>
                <span className="font-semibold">{bookingInfo.teacher_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Subject:</span>
                <span className="font-semibold">{bookingInfo.subject}</span>
              </div>
              {bookingInfo.section && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Section:</span>
                  <span className="font-semibold">{bookingInfo.section}</span>
                </div>
              )}
              {bookingInfo.chapter && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Chapter:</span>
                  <span className="font-semibold">{bookingInfo.chapter}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-600">Date:</span>
                <span className="font-semibold">{new Date(bookingInfo.booking_date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Time:</span>
                <span className="font-semibold">{bookingInfo.start_time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Duration:</span>
                <span className="font-semibold">{bookingInfo.duration_hours} hour(s)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Places:</span>
                <span className="font-semibold" data-testid="payment-group-size">
                  {(bookingInfo.group_size || 1) === 1 ? '1 student' : `${bookingInfo.group_size} students`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Rate:</span>
                <span className="font-semibold">₹{bookingInfo.rate_per_hour}/hr</span>
              </div>
              {(bookingInfo.group_size || 1) > 1 && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Visibility:</span>
                  <span className="font-semibold" data-testid="payment-visibility">
                    {bookingInfo.visibility === 'discoverable' ? 'Discoverable' : 'Invite only'}
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Amount:</span>
                <span className="text-3xl font-bold text-violet-600" data-testid="payment-total">₹{bookingInfo.total_amount.toFixed(2)}</span>
              </div>
              {(bookingInfo.group_size || 1) > 1 && (
                <p className="text-xs text-slate-500 mt-2">
                  Covers all {bookingInfo.group_size} places. Students you accept later won't pay through the platform.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* UPI Payment */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Pay via UPI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {settings?.upi_qr_code_url ? (
              <div className="text-center">
                <div className="bg-white border-4 border-violet-200 rounded-2xl p-4 inline-block">
                  <img
                    src={settings.upi_qr_code_url.startsWith('http') ? settings.upi_qr_code_url : `${API.replace(/\/api$/, '')}${settings.upi_qr_code_url}`}
                    alt="UPI QR Code"
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                <p className="mt-4 text-sm text-slate-600">
                  Scan this QR code with any UPI app to complete payment
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-600">UPI QR code not configured. Please contact admin.</p>
              </div>
            )}

            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> After paying, tap the button below. Your booking will be
                  confirmed once our finance team verifies the payment — you'll be notified here and by email.
                </p>
              </div>

              <Button
                onClick={handlePaymentComplete}
                disabled={processing}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 font-medium shadow-lg shadow-violet-500/25"
                data-testid="confirm-payment-button"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  'I Have Completed Payment'
                )}
              </Button>

              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="w-full rounded-full"
                disabled={processing}
              >
                Go Back
              </Button>
            </div>

            {supportCard}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Payment;
